import { isTopconManufacturer } from "./fluidManualBrush";
import { getManufacturerFromDataSet } from "./topconOptDisplayStretch";

export const DEFAULT_IMAGE_CONTRAST = 100;
export const DEFAULT_OPT_IMAGE_CONTRAST = 175;
export const MIN_IMAGE_CONTRAST = 0;
export const MAX_IMAGE_CONTRAST = 200;

function getManufacturerFromOpMetaData(opMetaData) {
  if (!opMetaData || !Array.isArray(opMetaData)) {
    return "";
  }

  const entry = opMetaData.find(
    (obj) => obj.id === "00080070" || obj.name === "Manufacturer",
  );
  return String(entry?.Value || "").trim();
}

export function resolveStackManufacturer(stackData, opMetaData) {
  const fromDataset =
    getManufacturerFromDataSet(stackData?.OP?.metadata?.dataset) ||
    getManufacturerFromDataSet(stackData?.OPT?.metadata?.dataset);

  if (fromDataset) {
    return fromDataset;
  }

  return getManufacturerFromOpMetaData(opMetaData);
}

export function shouldApplyOptImageContrast(stackData, opMetaData) {
  return isTopconManufacturer(resolveStackManufacturer(stackData, opMetaData));
}

export function hasTopconLoadedStack(dicomData) {
  if (!dicomData) {
    return false;
  }

  if (isTopconManufacturer(getManufacturerFromOpMetaData(dicomData.opMetaData))) {
    return true;
  }

  return Object.values(dicomData.dicom_data || {}).some((stack) =>
    shouldApplyOptImageContrast(stack, dicomData.opMetaData),
  );
}

export function getOptImageContrastValue(settings, stackData, opMetaData) {
  if (!shouldApplyOptImageContrast(stackData, opMetaData)) {
    return DEFAULT_IMAGE_CONTRAST;
  }

  return settings?.imageContrast?.value ?? DEFAULT_OPT_IMAGE_CONTRAST;
}

export function clampImageContrast(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return DEFAULT_IMAGE_CONTRAST;
  }
  return Math.min(MAX_IMAGE_CONTRAST, Math.max(MIN_IMAGE_CONTRAST, numeric));
}

export function contrastToWindowWidthScale(contrastValue) {
  const contrast = clampImageContrast(contrastValue);
  if (contrast === 0) {
    return 0.01;
  }
  return DEFAULT_IMAGE_CONTRAST / contrast;
}

export function applyContrastToVoi(baselineVoi, contrastValue) {
  if (!baselineVoi) {
    return null;
  }

  const contrast = clampImageContrast(contrastValue);
  if (contrast === DEFAULT_IMAGE_CONTRAST) {
    return {
      windowCenter: baselineVoi.windowCenter,
      windowWidth: baselineVoi.windowWidth,
    };
  }

  const scale = contrastToWindowWidthScale(contrast);
  return {
    windowCenter: baselineVoi.windowCenter,
    windowWidth: Math.max(1, baselineVoi.windowWidth * scale),
  };
}

export function captureBaselineVoiFromViewport(viewport) {
  if (!viewport?.voi) {
    return null;
  }

  return {
    windowCenter: viewport.voi.windowCenter,
    windowWidth: viewport.voi.windowWidth,
  };
}

export function captureBaselineVoiFromElement(cornerstone, element) {
  if (!cornerstone || !element) {
    return null;
  }

  try {
    cornerstone.getEnabledElement(element);
    return captureBaselineVoiFromViewport(cornerstone.getViewport(element));
  } catch (_error) {
    return null;
  }
}

export function captureBaselineVoiFromImage(cornerstone, element, image) {
  const safeVoi = resolveSafeViewportVoi(image);
  if (safeVoi) {
    return safeVoi;
  }

  return captureBaselineVoiFromElement(cornerstone, element);
}

export function applyImageContrastToElement(
  cornerstone,
  element,
  contrastValue,
  baselineVoi,
) {
  if (!cornerstone || !element || !baselineVoi) {
    return false;
  }

  try {
    cornerstone.getEnabledElement(element);
  } catch (_error) {
    return false;
  }

  const nextVoi = applyContrastToVoi(baselineVoi, contrastValue);
  if (!nextVoi) {
    return false;
  }

  const viewport = cornerstone.getViewport(element);
  viewport.voi = nextVoi;
  cornerstone.setViewport(element, viewport);
  cornerstone.updateImage(element);
  return true;
}

export function resolveSafeViewportVoi(image) {
  const wc = Array.isArray(image.windowCenter)
    ? image.windowCenter[0]
    : image.windowCenter;
  const ww = Array.isArray(image.windowWidth)
    ? image.windowWidth[0]
    : image.windowWidth;
  const min = image.minPixelValue;
  const max = image.maxPixelValue;

  if (
    typeof min === "number" &&
    typeof max === "number" &&
    (typeof wc !== "number" ||
      typeof ww !== "number" ||
      ww <= 0 ||
      wc < min ||
      wc > max)
  ) {
    return {
      windowCenter: (min + max) / 2,
      windowWidth: Math.max(1, max - min),
    };
  }

  if (typeof wc === "number" && typeof ww === "number" && ww > 0) {
    return {
      windowCenter: wc,
      windowWidth: ww,
    };
  }

  return null;
}
